import { useMemo } from "react";
import { useAppToast } from "../design-system/components/AppToastProvider";

/**
 * Toasts der Anwendung.
 *
 * Es gibt genau einen Toast in TimeTab: den aus dem Design System
 * (AppToastProvider / AppToast). Dieser Hook ist nur noch die vertraute
 * Aufrufform davor — success/error/warning/info/loading/close —, damit die
 * Aufrufstellen unveraendert bleiben.
 *
 * Optionen: `duration` in Millisekunden. `autoHideDuration` und `persist`
 * stammen aus der frueheren notistack-Zeit und werden weiter verstanden, damit
 * bestehende Aufrufe nicht stillschweigend ihre Laufzeit verlieren.
 */
function toDsOptions(options = {}) {
  const { persist, autoHideDuration, duration, variant, ...rest } = options || {};
  const resolvedDuration = persist ? null : duration ?? autoHideDuration;

  return {
    ...rest,
    ...(variant ? { variant } : {}),
    ...(resolvedDuration === undefined ? {} : { duration: resolvedDuration }),
  };
}

export default function useToast(defaultOptions = {}) {
  const toast = useAppToast();

  return useMemo(() => {
    const call = (method, message, options) =>
      method(message, toDsOptions({ ...defaultOptions, ...options }));

    return {
      show: (message, options) => call(toast.show, message, options),
      success: (message, options) => call(toast.success, message, options),
      info: (message, options) => call(toast.info, message, options),
      warning: (message, options) => call(toast.warning, message, options),
      error: (message, options) => call(toast.error, message, options),
      // Bleibt stehen, bis close(id) ihn wegnimmt.
      loading: (message = "Laden...", options = {}) =>
        call(toast.info, message, { ...options, persist: true }),
      close: (id) => (id == null ? toast.clear() : toast.dismiss(id)),
    };
    // defaultOptions kommt an den meisten Aufrufstellen als Literal herein und
    // waere damit bei jedem Rendern neu — deshalb bewusst nicht in der Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);
}
